import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { buildRoute, ROUTES } from '../../constants/routes';

// Shared style for the small metadata pills under the title (param count,
// sample type, TAT, fasting, NABL). Keeping them visually consistent so
// the card reads as one info strip rather than five different shapes.
const chipSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.375,
  bgcolor: 'rgba(21,42,30,0.04)',
  border: '1px solid var(--border-l)',
  borderRadius: 100,
  px: 0.875,
  py: 0.375,
  fontSize: 10.5,
  fontWeight: 600,
  color: 'var(--stone)',
  lineHeight: 1,
};

const TestCard = ({ test }) => {
  const navigate = useNavigate();
  const goToDetail = () => navigate(buildRoute(ROUTES.TEST_DETAIL, { id: test.id }));

  const discount = test.original && test.price
    ? Math.round(((test.original - test.price) / test.original) * 100)
    : null;

  return (
    <Box
      onClick={goToDetail}
      sx={{
        bgcolor: '#fff',
        border: '1px solid var(--border-l)',
        borderRadius: '20px',
        p: { xs: 2.25, md: 2.5 },
        cursor: 'pointer',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s cubic-bezier(0.23,1,0.32,1)',
        height: '100%',
        '&:hover': {
          borderColor: 'var(--sage-p)',
          transform: 'translateY(-5px)',
          boxShadow: '0 12px 32px rgba(21,42,30,0.1)',
        },
      }}
    >
      {/* Save badge — top right */}
      {discount !== null && discount > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.375,
            bgcolor: 'rgba(217,119,6,0.12)',
            border: '1px solid rgba(217,119,6,0.3)',
            color: 'var(--amber)',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.04em',
            borderRadius: 100,
            px: 1,
            py: 0.375,
            lineHeight: 1,
          }}
        >
          <Box component="span" sx={{ lineHeight: 1 }}>★</Box>
          <Box component="span" sx={{ lineHeight: 1 }}>Save {discount}%</Box>
        </Box>
      )}

      {/* Icon */}
      <Typography sx={{ fontSize: 28, mb: 1.5, lineHeight: 1 }}>{test.icon}</Typography>

      {/* Tag pill */}
      {test.tag && (
        <Box
          sx={{
            display: 'inline-flex',
            alignSelf: 'flex-start',
            bgcolor: 'rgba(106,174,128,0.15)',
            color: 'var(--sage)',
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.03em',
            borderRadius: 100,
            px: 1.125,
            py: 0.375,
            mb: 1,
            lineHeight: 1,
          }}
        >
          {test.tag}
        </Box>
      )}

      {/* Title */}
      <Typography
        sx={{
          fontSize: 15,
          fontWeight: 700,
          color: 'var(--ink)',
          lineHeight: 1.3,
          mb: 0.5,
        }}
      >
        {test.name}
      </Typography>

      {/* Question / card tag */}
      {test.cardTag && (
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--sage)',
            mb: 1.25,
            lineHeight: 1.4,
          }}
        >
          {test.cardTag}
        </Typography>
      )}

      {/* Outcome bullets */}
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', mb: 1.25 }}>
        {(test.outcomes || []).slice(0, 3).map((o, i) => (
          <Box
            component="li"
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 0.625,
              fontSize: 11,
              color: 'var(--stone)',
              lineHeight: 1.5,
              mb: 0.5,
            }}
          >
            <Box
              component="span"
              sx={{
                color: 'var(--sage)',
                fontWeight: 700,
                flexShrink: 0,
                mt: '2px',
                lineHeight: 1,
              }}
            >
              ·
            </Box>
            <Box component="span">{o}</Box>
          </Box>
        ))}
      </Box>

      {/* Test metadata strip — enriched from EDOS on the backend (param
          count, sample type, TAT, fasting). Each chip only renders when
          the underlying field is present, so cards degrade gracefully if
          EDOS is unreachable. */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          mb: 1.25,
          flex: 1,
          alignContent: 'flex-start',
        }}
      >
        {test.paramCount > 0 && (
          <Box sx={chipSx}>
            <Box component="span" sx={{ lineHeight: 1 }}>🧬</Box>
            <Box component="span" sx={{ lineHeight: 1 }}>
              {test.paramCount} param{test.paramCount === 1 ? '' : 's'}
            </Box>
          </Box>
        )}
        {test.sample && (
          <Box sx={chipSx}>
            <Box component="span" sx={{ lineHeight: 1 }}>💉</Box>
            <Box component="span" sx={{ lineHeight: 1 }}>{test.sample}</Box>
          </Box>
        )}
        {test.tat && (
          <Box sx={chipSx}>
            <Box component="span" sx={{ lineHeight: 1 }}>⏱</Box>
            <Box component="span" sx={{ lineHeight: 1 }}>{test.tat}</Box>
          </Box>
        )}
        {test.fasting && (
          <Box sx={chipSx}>
            <Box component="span" sx={{ lineHeight: 1 }}>🍽</Box>
            <Box component="span" sx={{ lineHeight: 1 }}>Fasting</Box>
          </Box>
        )}
        {test.nabl && (
          <Box sx={{ ...chipSx, color: 'var(--sage)', bgcolor: 'rgba(106,174,128,0.12)' }}>
            <Box component="span" sx={{ lineHeight: 1 }}>🏥</Box>
            <Box component="span" sx={{ lineHeight: 1 }}>NABL</Box>
          </Box>
        )}
      </Box>

      {/* Price row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 1,
          flexWrap: 'wrap',
          mb: 1.25,
        }}
      >
        <Typography
          component="span"
          className="serif"
          sx={{ fontSize: 22, fontWeight: 700, color: 'var(--forest)', lineHeight: 1 }}
        >
          ₹{test.price}
        </Typography>
        {discount !== null && discount > 0 && (
          <>
            <Typography
              component="span"
              sx={{
                fontSize: 12,
                textDecoration: 'line-through',
                color: 'var(--stone)',
                lineHeight: 1,
              }}
            >
              ₹{test.original}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--sage)',
                bgcolor: 'rgba(106,174,128,0.12)',
                borderRadius: 100,
                px: 0.875,
                py: 0.25,
                lineHeight: 1.2,
              }}
            >
              Save {discount}%
            </Typography>
          </>
        )}
      </Box>

      {/* View Details button */}
      <Button
        onClick={(e) => {
          e.stopPropagation();
          goToDetail();
        }}
        sx={{
          width: '100%',
          bgcolor: 'var(--forest)',
          color: '#fff',
          borderRadius: 100,
          fontSize: 12.5,
          fontWeight: 700,
          textTransform: 'none',
          py: 0.875,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.625,
          '&:hover': { bgcolor: 'var(--ink)' },
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 14, display: 'block' }} />
        View Details
      </Button>
    </Box>
  );
};

export default TestCard;
